import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Bell } from "lucide-react";
import { cn } from "@/lib/utils";

export default function NotificationsBell() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 15000,
  });
  const unread = items.filter((n) => !n.read_at).length;

  const readAll = useMutation({
    mutationFn: () => api.post("/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readOne = useMutation({
    mutationFn: (id) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative w-9 h-9 grid place-items-center rounded-md hover:bg-zinc-100 transition-colors"
          aria-label="Notifications"
          data-testid="notifications-trigger"
        >
          <Bell className="w-4 h-4 text-zinc-600" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1"
              data-testid="notifications-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Notifications</div>
            <div className="font-heading font-bold" data-testid="notifications-header">
              {unread > 0 ? `${unread} unread` : items.length > 0 ? `${items.length} · all read` : "All caught up"}
            </div>
          </div>
          {unread > 0 && (
            <button onClick={() => readAll.mutate()} className="text-xs text-zinc-600 hover:text-zinc-900 underline underline-offset-2" data-testid="notifications-mark-all">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">Nothing yet.</div>}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                if (!n.read_at) readOne.mutate(n.id);
                if (n.link) nav(n.link);
              }}
              className={cn(
                "w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors",
                !n.read_at && "bg-blue-50/40",
              )}
              data-testid={`notification-${n.id}`}
            >
              <div className="flex items-start gap-2">
                {!n.read_at && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono mt-1">{n.created_at.slice(0, 16).replace("T", " ")}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
